/**
 * ChartFactory Component
 *
 * Dynamically renders the appropriate chart component based on chart type.
 * Acts as a router to BigTextBlock, LineChart, BarChart, etc.
 */

import PropTypes from 'prop-types';
import BigTextBlock from './BigTextBlock';
import LineChart from './LineChart';
import BarChart from './BarChart';
import LineBarChart from './LineBarChart';
import DualLineChart from './DualLineChart';
import TripleLineChart from './TripleLineChart';

const ChartFactory = ({ chartId, chartType }) => {
  if (!chartId) {
    return (
      <div className="chart-error">
        <p>Error: No chart ID provided</p>
      </div>
    );
  }

  // Normalize chart type (handle both "line" and "line_chart" formats)
  const normalizeType = (type) => {
    const typeMap = {
      'line': 'line_chart',
      'bar': 'bar_chart',
      'line_bar': 'line_bar_chart',
      'dual_line': 'dual_line_chart',
      'triple_line': 'triple_line_chart',
      'growth': 'growth_chart',
      'area': 'area_chart',
      'pie': 'pie_chart',
      'scatter': 'scatter_plot'
    };
    return typeMap[type] || type;
  };

  const normalizedType = normalizeType(chartType);

  // Route to appropriate component based on chart type
  switch (normalizedType) {
    case 'big_text_block':
      return <BigTextBlock chartId={chartId} />;

    case 'line_chart':
    case 'area_chart':
    case 'growth_chart':
      return <LineChart chartId={chartId} />;

    case 'bar_chart':
      return <BarChart chartId={chartId} />;

    case 'line_bar_chart':
      return <LineBarChart chartId={chartId} />;

    case 'dual_line_chart':
      return <DualLineChart chartId={chartId} />;

    case 'triple_line_chart':
      return <TripleLineChart chartId={chartId} />;

    // TODO: Implement remaining chart types
    // case 'table':
    //   return <TableChart chartId={chartId} />;

    // case 'pie_chart':
    //   return <PieChart chartId={chartId} />;

    default:
      return (
        <div className="chart-not-implemented">
          <h4>Chart Type: {normalizedType}</h4>
          <p>This chart type is not yet implemented.</p>
          <p className="chart-id-label">Chart ID: {chartId}</p>
        </div>
      );
  }
};

ChartFactory.propTypes = {
  chartId: PropTypes.string.isRequired,
  chartType: PropTypes.oneOf([
    'big_text_block',
    'line_chart',
    'bar_chart',
    'line_bar_chart',
    'dual_line_chart',
    'growth_chart',
    'table',
    'pie_chart',
    'area_chart',
    'scatter_plot',
    // Also accept short forms from YAML
    'line',
    'bar',
    'line_bar',
    'dual_line',
    'triple_line',
    'triple_line_chart',
    'growth',
    'area',
    'pie',
    'scatter',
  ]),
};

ChartFactory.defaultProps = {
  chartType: 'big_text_block',
};

export default ChartFactory;
